/**
 * Pricing Templates Configuration Component
 * 
 * Allows creating, naming, and managing multiple pricing templates.
 * Integrates with FC Optimizer for actual fulfillment cost data.
 * Uses Shopify Polaris design patterns.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
    Card,
    Form,
    FormLayout,
    TextField,
    Select,
    Button,
    Stack,
    Badge,
    Banner,
    Modal,
    DataTable,
    Tabs,
    TextContainer,
    Spinner,
    Toast
} from './polaris-compat';
import { db } from 'config/firebase.js';
import { doc, getDoc, setDoc, collection, getDocs, deleteDoc, query, orderBy } from 'firebase/firestore';
import './PricingRulesConfig.css';

const PricingRulesConfig = () => {
    // State
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [templates, setTemplates] = useState([]);
    const [selectedTemplateId, setSelectedTemplateId] = useState(null);
    const [currentTemplate, setCurrentTemplate] = useState(null);
    const [selectedTab, setSelectedTab] = useState(0);
    const [toast, setToast] = useState(null);

    // FC Baseline aggregated costs
    const [fcCosts, setFcCosts] = useState(null);
    const [fcLoading, setFcLoading] = useState(false);

    // Modal states
    const [templateModalOpen, setTemplateModalOpen] = useState(false);
    const [categoryModalOpen, setCategoryModalOpen] = useState(false);
    const [skuModalOpen, setSkuModalOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [editingSku, setEditingSku] = useState(null);

    // Form states
    const [templateForm, setTemplateForm] = useState({ name: '', description: '' });
    const [categoryForm, setCategoryForm] = useState({ category: '', multiplier: '', minMargin: '', notes: '' });
    const [skuForm, setSkuForm] = useState({ sku: '', multiplier: '', minMargin: '', fixedPrice: '', reason: '' });

    // Load templates on mount
    useEffect(() => {
        loadTemplates();
        loadFCCosts();
    }, []);

    // Load all templates
    const loadTemplates = async () => {
        setLoading(true);
        try {
            const q = query(collection(db, 'settings', 'pricing', 'templates'), orderBy('createdAt', 'desc'));
            const snapshot = await getDocs(q);

            const templateList = snapshot.docs.map(docSnap => ({
                id: docSnap.id,
                ...docSnap.data()
            }));

            // If no templates exist, create a default one
            if (templateList.length === 0) {
                const defaultTemplate = await createDefaultTemplate();
                templateList.push(defaultTemplate);
            }

            setTemplates(templateList);

            // Select first template by default
            if (templateList.length > 0 && !selectedTemplateId) {
                setSelectedTemplateId(templateList[0].id);
                setCurrentTemplate(templateList[0]);
            }
        } catch (error) {
            console.error('Error loading pricing templates:', error);
            setToast({ content: 'Error loading pricing templates', error: true });
        }
        setLoading(false);
    };

    // Load aggregated FC costs
    const loadFCCosts = async () => {
        setFcLoading(true);
        try {
            const snapshot = await getDocs(collection(db, 'fcBaseline'));

            // Aggregate costs by category/classification
            const costsByCategory = {};
            const allCosts = [];

            snapshot.docs.forEach(docSnap => {
                const data = docSnap.data();
                if (data.fulfillmentCost) {
                    allCosts.push(data.fulfillmentCost);

                    const category = data.category || 'uncategorized';
                    if (!costsByCategory[category]) {
                        costsByCategory[category] = [];
                    }
                    costsByCategory[category].push(data.fulfillmentCost);
                }
            });

            // Calculate averages
            const categoryAverages = {};
            Object.entries(costsByCategory).forEach(([cat, costs]) => {
                categoryAverages[cat] = {
                    average: costs.reduce((a, b) => a + b, 0) / costs.length,
                    min: Math.min(...costs),
                    max: Math.max(...costs),
                    count: costs.length
                };
            });

            setFcCosts({
                overall: allCosts.length > 0 ? {
                    average: allCosts.reduce((a, b) => a + b, 0) / allCosts.length,
                    min: Math.min(...allCosts),
                    max: Math.max(...allCosts),
                    count: allCosts.length
                } : null,
                byCategory: categoryAverages
            });
        } catch (error) {
            console.error('Error loading FC costs:', error);
        }
        setFcLoading(false);
    };

    // Create default template
    const createDefaultTemplate = async () => {
        const defaultTemplate = {
            id: 'default',
            name: 'Default Template',
            description: 'Standard pricing configuration',
            defaultMultiplier: 1.45,
            minimumMarginPercent: 20,
            targetMarginPercent: 45,
            psychologicalPricing: {
                enabled: true,
                preferredEnding: 0.99
            },
            useFCBaseline: true, // Use actual FC costs when available
            fallbackFulfillmentCost: 6.50, // Fallback when no FC data
            categoryRules: {},
            skuRules: {},
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
        };

        const docRef = doc(db, 'settings', 'pricing', 'templates', 'default');
        await setDoc(docRef, defaultTemplate);

        return defaultTemplate;
    };

    // Handle template selection
    const handleTemplateChange = (templateId) => {
        const template = templates.find(t => t.id === templateId);
        setSelectedTemplateId(templateId);
        setCurrentTemplate(template);
    };

    // Create new template
    const handleCreateTemplate = async () => {
        if (!templateForm.name.trim()) {
            setToast({ content: 'Please enter a template name', error: true });
            return;
        }

        const id = templateForm.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');

        const newTemplate = {
            id,
            name: templateForm.name,
            description: templateForm.description,
            defaultMultiplier: 1.45,
            minimumMarginPercent: 20,
            targetMarginPercent: 45,
            psychologicalPricing: {
                enabled: true,
                preferredEnding: 0.99
            },
            useFCBaseline: true,
            fallbackFulfillmentCost: 6.50,
            categoryRules: {},
            skuRules: {},
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
        };

        try {
            const docRef = doc(db, 'settings', 'pricing', 'templates', id);
            await setDoc(docRef, newTemplate);

            setTemplates(prev => [newTemplate, ...prev]);
            setSelectedTemplateId(id);
            setCurrentTemplate(newTemplate);
            setTemplateModalOpen(false);
            setTemplateForm({ name: '', description: '' });
            setToast({ content: `Created template: ${templateForm.name}` });
        } catch (error) {
            console.error('Error creating template:', error);
            setToast({ content: 'Error creating template', error: true });
        }
    };

    // Delete template
    const handleDeleteTemplate = async () => {
        if (selectedTemplateId === 'default') {
            setToast({ content: 'Cannot delete the default template', error: true });
            return;
        }

        try {
            await deleteDoc(doc(db, 'settings', 'pricing', 'templates', selectedTemplateId));

            setTemplates(prev => prev.filter(t => t.id !== selectedTemplateId));
            setDeleteModalOpen(false);

            // Select default template
            const defaultT = templates.find(t => t.id === 'default') || templates[0];
            if (defaultT) {
                setSelectedTemplateId(defaultT.id);
                setCurrentTemplate(defaultT);
            }

            setToast({ content: 'Template deleted' });
        } catch (error) {
            console.error('Error deleting template:', error);
            setToast({ content: 'Error deleting template', error: true });
        }
    };

    // Save current template
    const saveTemplate = async (updatedTemplate = currentTemplate) => {
        if (!updatedTemplate) return;

        setSaving(true);
        try {
            const toSave = {
                ...updatedTemplate,
                updatedAt: new Date().toISOString()
            };

            const docRef = doc(db, 'settings', 'pricing', 'templates', updatedTemplate.id);
            await setDoc(docRef, toSave);

            setCurrentTemplate(toSave);
            setTemplates(prev => prev.map(t => t.id === toSave.id ? toSave : t));
            setToast({ content: 'Template saved successfully' });
        } catch (error) {
            console.error('Error saving template:', error);
            setToast({ content: 'Error saving template', error: true });
        }
        setSaving(false);
    };

    // Handle field changes
    const handleFieldChange = (field, value) => {
        const updated = { ...currentTemplate, [field]: parseFloat(value) || value };
        setCurrentTemplate(updated);
    };

    const handlePsychologicalChange = (field, value) => {
        const updated = {
            ...currentTemplate,
            psychologicalPricing: {
                ...currentTemplate.psychologicalPricing,
                [field]: field === 'enabled' ? value === 'true' : parseFloat(value)
            }
        };
        setCurrentTemplate(updated);
    };

    // Category rule handlers
    const openCategoryModal = (category = null) => {
        if (category) {
            const rule = currentTemplate.categoryRules[category];
            setCategoryForm({
                category,
                multiplier: rule.multiplier?.toString() || '',
                minMargin: rule.minimumMarginPercent?.toString() || '',
                notes: rule.notes || ''
            });
            setEditingCategory(category);
        } else {
            setCategoryForm({ category: '', multiplier: currentTemplate.defaultMultiplier.toString(), minMargin: '', notes: '' });
            setEditingCategory(null);
        }
        setCategoryModalOpen(true);
    };

    const saveCategoryRule = () => {
        const updated = {
            ...currentTemplate,
            categoryRules: {
                ...currentTemplate.categoryRules,
                [categoryForm.category]: {
                    multiplier: parseFloat(categoryForm.multiplier),
                    minimumMarginPercent: parseFloat(categoryForm.minMargin) || currentTemplate.minimumMarginPercent,
                    notes: categoryForm.notes,
                    updatedAt: new Date().toISOString()
                }
            }
        };
        saveTemplate(updated);
        setCategoryModalOpen(false);
    };

    const deleteCategoryRule = (category) => {
        const { [category]: removed, ...remaining } = currentTemplate.categoryRules;
        const updated = { ...currentTemplate, categoryRules: remaining };
        saveTemplate(updated);
    };

    // SKU rule handlers
    const openSkuModal = (sku = null) => {
        if (sku) {
            const rule = currentTemplate.skuRules[sku];
            setSkuForm({
                sku,
                multiplier: rule.multiplier?.toString() || '',
                minMargin: rule.minimumMarginPercent?.toString() || '',
                fixedPrice: rule.fixedPrice?.toString() || '',
                reason: rule.reason || ''
            });
            setEditingSku(sku);
        } else {
            setSkuForm({ sku: '', multiplier: '', minMargin: '', fixedPrice: '', reason: '' });
            setEditingSku(null);
        }
        setSkuModalOpen(true);
    };

    const saveSkuRule = () => {
        const updated = {
            ...currentTemplate,
            skuRules: {
                ...currentTemplate.skuRules,
                [skuForm.sku]: {
                    multiplier: skuForm.multiplier ? parseFloat(skuForm.multiplier) : null,
                    minimumMarginPercent: skuForm.minMargin ? parseFloat(skuForm.minMargin) : null,
                    fixedPrice: skuForm.fixedPrice ? parseFloat(skuForm.fixedPrice) : null,
                    reason: skuForm.reason,
                    updatedAt: new Date().toISOString()
                }
            }
        };
        saveTemplate(updated);
        setSkuModalOpen(false);
    };

    const deleteSkuRule = (sku) => {
        const { [sku]: removed, ...remaining } = currentTemplate.skuRules;
        const updated = { ...currentTemplate, skuRules: remaining };
        saveTemplate(updated);
    };

    // Render loading state
    if (loading || !currentTemplate) {
        return (
            <div className="pricing-rules-loading">
                <Spinner size="large" />
                <p>Loading pricing templates...</p>
            </div>
        );
    }

    // Tab panels
    const tabs = [
        { id: 'settings', content: 'Template Settings', panelID: 'settings-panel' },
        { id: 'categories', content: 'Category Rules', panelID: 'categories-panel' },
        { id: 'skus', content: 'SKU Overrides', panelID: 'skus-panel' }
    ];

    return (
        <div className="pricing-rules-config">
            <div className="page-header">
                <h1>Pricing Templates</h1>
                <p className="subtitle">Create and manage multiple pricing configurations</p>
            </div>

            {/* Template Selector */}
            <Card sectioned>
                <Stack distribution="equalSpacing" alignment="center">
                    <Stack spacing="tight" alignment="center">
                        <Select
                            label=""
                            options={templates.map(t => ({ label: t.name, value: t.id }))}
                            value={selectedTemplateId}
                            onChange={handleTemplateChange}
                        />
                        <Badge status={selectedTemplateId === 'default' ? 'info' : 'success'}>
                            {currentTemplate?.name}
                        </Badge>
                    </Stack>
                    <Stack spacing="tight">
                        <Button onClick={() => setTemplateModalOpen(true)}>New Template</Button>
                        {selectedTemplateId !== 'default' && (
                            <Button destructive onClick={() => setDeleteModalOpen(true)}>Delete</Button>
                        )}
                    </Stack>
                </Stack>
                {currentTemplate?.description && (
                    <p className="template-description">{currentTemplate.description}</p>
                )}
            </Card>

            {/* FC Cost Banner */}
            {fcCosts?.overall && (
                <Banner status="info" title="Fulfillment Costs from FC Optimizer">
                    <Stack distribution="equalSpacing">
                        <div>
                            <strong>Average:</strong> ${fcCosts.overall.average.toFixed(2)}
                        </div>
                        <div>
                            <strong>Range:</strong> ${fcCosts.overall.min.toFixed(2)} - ${fcCosts.overall.max.toFixed(2)}
                        </div>
                        <div>
                            <strong>Products:</strong> {fcCosts.overall.count.toLocaleString()}
                        </div>
                    </Stack>
                </Banner>
            )}

            {/* Pricing Formula */}
            <Banner status="info" title="Pricing Formula">
                <p><strong>(COGS + Fulfillment Cost) × Multiplier = Shopify Price</strong></p>
                <p>Priority: SKU Override → Category Rule → Template Default</p>
                {currentTemplate?.useFCBaseline && (
                    <p><em>Using actual FC costs when available, fallback: ${currentTemplate.fallbackFulfillmentCost}</em></p>
                )}
            </Banner>

            <Tabs tabs={tabs} selected={selectedTab} onSelect={setSelectedTab}>
                {/* Template Settings Tab */}
                {selectedTab === 0 && (
                    <Card title="Template Settings" sectioned>
                        <FormLayout>
                            <TextField
                                label="Template Name"
                                value={currentTemplate.name}
                                onChange={(value) => handleFieldChange('name', value)}
                                disabled={selectedTemplateId === 'default'}
                            />

                            <TextField
                                label="Description"
                                value={currentTemplate.description || ''}
                                onChange={(value) => handleFieldChange('description', value)}
                                multiline={2}
                            />

                            <FormLayout.Group>
                                <TextField
                                    label="Default Multiplier"
                                    type="number"
                                    value={currentTemplate.defaultMultiplier?.toString() || '1.45'}
                                    onChange={(value) => handleFieldChange('defaultMultiplier', value)}
                                    suffix="×"
                                    helpText="Applied to (COGS + Fulfillment Cost)"
                                    step="0.01"
                                />

                                <TextField
                                    label="Minimum Margin %"
                                    type="number"
                                    value={currentTemplate.minimumMarginPercent?.toString() || '20'}
                                    onChange={(value) => handleFieldChange('minimumMarginPercent', value)}
                                    suffix="%"
                                    helpText="Products below this will be flagged"
                                />

                                <TextField
                                    label="Target Margin %"
                                    type="number"
                                    value={currentTemplate.targetMarginPercent?.toString() || '45'}
                                    onChange={(value) => handleFieldChange('targetMarginPercent', value)}
                                    suffix="%"
                                    helpText="Ideal margin goal"
                                />
                            </FormLayout.Group>

                            <h3 style={{ marginTop: '24px', marginBottom: '12px' }}>Fulfillment Costs</h3>

                            <FormLayout.Group>
                                <Select
                                    label="Use FC Optimizer Data"
                                    options={[
                                        { label: 'Yes - Use actual costs when available', value: 'true' },
                                        { label: 'No - Use fallback only', value: 'false' }
                                    ]}
                                    value={currentTemplate.useFCBaseline?.toString() || 'true'}
                                    onChange={(value) => handleFieldChange('useFCBaseline', value === 'true')}
                                />

                                <TextField
                                    label="Fallback Fulfillment Cost"
                                    type="number"
                                    value={currentTemplate.fallbackFulfillmentCost?.toString() || '6.50'}
                                    onChange={(value) => handleFieldChange('fallbackFulfillmentCost', value)}
                                    prefix="$"
                                    helpText="Used when no FC data exists"
                                    step="0.50"
                                />
                            </FormLayout.Group>

                            <h3 style={{ marginTop: '24px', marginBottom: '12px' }}>Psychological Pricing</h3>

                            <FormLayout.Group>
                                <Select
                                    label="Enable Psychological Pricing"
                                    options={[
                                        { label: 'Yes', value: 'true' },
                                        { label: 'No', value: 'false' }
                                    ]}
                                    value={currentTemplate.psychologicalPricing?.enabled?.toString() || 'true'}
                                    onChange={(value) => handlePsychologicalChange('enabled', value)}
                                />

                                <Select
                                    label="Preferred Price Ending"
                                    options={[
                                        { label: '.99 (e.g., $24.99)', value: '0.99' },
                                        { label: '.95 (e.g., $24.95)', value: '0.95' },
                                        { label: '.49 (e.g., $24.49)', value: '0.49' }
                                    ]}
                                    value={currentTemplate.psychologicalPricing?.preferredEnding?.toString() || '0.99'}
                                    onChange={(value) => handlePsychologicalChange('preferredEnding', value)}
                                    disabled={!currentTemplate.psychologicalPricing?.enabled}
                                />
                            </FormLayout.Group>

                            <div className="form-actions">
                                <Button primary onClick={() => saveTemplate()} loading={saving}>
                                    Save Template
                                </Button>
                            </div>
                        </FormLayout>
                    </Card>
                )}

                {/* Category Rules Tab */}
                {selectedTab === 1 && (
                    <Card title="Category-Level Pricing Rules" sectioned>
                        <div className="section-header">
                            <p>Override pricing for specific product categories.</p>
                            <Button onClick={() => openCategoryModal()}>Add Category Rule</Button>
                        </div>

                        {Object.keys(currentTemplate.categoryRules || {}).length === 0 ? (
                            <div className="empty-state">
                                <p>No category rules defined. All products use template defaults.</p>
                            </div>
                        ) : (
                            <DataTable
                                columnContentTypes={['text', 'numeric', 'numeric', 'text', 'text']}
                                headings={['Category', 'Multiplier', 'Min Margin', 'Notes', 'Actions']}
                                rows={Object.entries(currentTemplate.categoryRules).map(([category, rule]) => [
                                    category,
                                    `${rule.multiplier}×`,
                                    `${rule.minimumMarginPercent || currentTemplate.minimumMarginPercent}%`,
                                    rule.notes || '-',
                                    <Stack spacing="tight" key={category}>
                                        <Button size="slim" onClick={() => openCategoryModal(category)}>Edit</Button>
                                        <Button size="slim" destructive onClick={() => deleteCategoryRule(category)}>Delete</Button>
                                    </Stack>
                                ])}
                            />
                        )}
                    </Card>
                )}

                {/* SKU Overrides Tab */}
                {selectedTab === 2 && (
                    <Card title="SKU-Level Price Overrides" sectioned>
                        <div className="section-header">
                            <p>Override pricing for specific SKUs. Highest priority.</p>
                            <Button onClick={() => openSkuModal()}>Add SKU Override</Button>
                        </div>

                        {Object.keys(currentTemplate.skuRules || {}).length === 0 ? (
                            <div className="empty-state">
                                <p>No SKU overrides defined.</p>
                            </div>
                        ) : (
                            <DataTable
                                columnContentTypes={['text', 'numeric', 'numeric', 'text', 'text']}
                                headings={['SKU', 'Multiplier / Fixed', 'Min Margin', 'Reason', 'Actions']}
                                rows={Object.entries(currentTemplate.skuRules).map(([sku, rule]) => [
                                    sku,
                                    rule.fixedPrice ? `$${rule.fixedPrice} (fixed)` : `${rule.multiplier}×`,
                                    rule.minimumMarginPercent ? `${rule.minimumMarginPercent}%` : '-',
                                    rule.reason || '-',
                                    <Stack spacing="tight" key={sku}>
                                        <Button size="slim" onClick={() => openSkuModal(sku)}>Edit</Button>
                                        <Button size="slim" destructive onClick={() => deleteSkuRule(sku)}>Delete</Button>
                                    </Stack>
                                ])}
                            />
                        )}
                    </Card>
                )}
            </Tabs>

            {/* Create Template Modal */}
            <Modal
                open={templateModalOpen}
                onClose={() => setTemplateModalOpen(false)}
                title="Create New Pricing Template"
                primaryAction={{
                    content: 'Create Template',
                    onAction: handleCreateTemplate
                }}
                secondaryActions={[
                    { content: 'Cancel', onAction: () => setTemplateModalOpen(false) }
                ]}
            >
                <Modal.Section>
                    <FormLayout>
                        <TextField
                            label="Template Name"
                            value={templateForm.name}
                            onChange={(value) => setTemplateForm({ ...templateForm, name: value })}
                            placeholder="e.g., Holiday Pricing, Clearance, Premium"
                        />

                        <TextField
                            label="Description"
                            value={templateForm.description}
                            onChange={(value) => setTemplateForm({ ...templateForm, description: value })}
                            placeholder="When to use this template..."
                            multiline={2}
                        />
                    </FormLayout>
                </Modal.Section>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal
                open={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                title="Delete Template?"
                primaryAction={{
                    content: 'Delete',
                    onAction: handleDeleteTemplate,
                    destructive: true
                }}
                secondaryActions={[
                    { content: 'Cancel', onAction: () => setDeleteModalOpen(false) }
                ]}
            >
                <Modal.Section>
                    <TextContainer>
                        <p>Are you sure you want to delete the template "{currentTemplate?.name}"?</p>
                        <p>This action cannot be undone.</p>
                    </TextContainer>
                </Modal.Section>
            </Modal>

            {/* Category Modal */}
            <Modal
                open={categoryModalOpen}
                onClose={() => setCategoryModalOpen(false)}
                title={editingCategory ? `Edit Rule: ${editingCategory}` : 'Add Category Rule'}
                primaryAction={{
                    content: 'Save',
                    onAction: saveCategoryRule
                }}
                secondaryActions={[
                    { content: 'Cancel', onAction: () => setCategoryModalOpen(false) }
                ]}
            >
                <Modal.Section>
                    <FormLayout>
                        <TextField
                            label="Category Name"
                            value={categoryForm.category}
                            onChange={(value) => setCategoryForm({ ...categoryForm, category: value })}
                            disabled={!!editingCategory}
                            placeholder="e.g., Candy, Beverages, Snacks"
                        />

                        <TextField
                            label="Multiplier"
                            type="number"
                            value={categoryForm.multiplier}
                            onChange={(value) => setCategoryForm({ ...categoryForm, multiplier: value })}
                            suffix="×"
                            step="0.01"
                        />

                        <TextField
                            label="Minimum Margin % (optional)"
                            type="number"
                            value={categoryForm.minMargin}
                            onChange={(value) => setCategoryForm({ ...categoryForm, minMargin: value })}
                            suffix="%"
                            placeholder={`Default: ${currentTemplate.minimumMarginPercent}%`}
                        />

                        <TextField
                            label="Notes"
                            value={categoryForm.notes}
                            onChange={(value) => setCategoryForm({ ...categoryForm, notes: value })}
                            placeholder="Reason for this rule..."
                            multiline={2}
                        />
                    </FormLayout>
                </Modal.Section>
            </Modal>

            {/* SKU Modal */}
            <Modal
                open={skuModalOpen}
                onClose={() => setSkuModalOpen(false)}
                title={editingSku ? `Edit Override: ${editingSku}` : 'Add SKU Override'}
                primaryAction={{
                    content: 'Save',
                    onAction: saveSkuRule
                }}
                secondaryActions={[
                    { content: 'Cancel', onAction: () => setSkuModalOpen(false) }
                ]}
            >
                <Modal.Section>
                    <FormLayout>
                        <TextField
                            label="SKU"
                            value={skuForm.sku}
                            onChange={(value) => setSkuForm({ ...skuForm, sku: value })}
                            disabled={!!editingSku}
                            placeholder="Enter product SKU"
                        />

                        <Banner status="info" title="Pricing Options">
                            <p>Set either a multiplier OR a fixed price (not both).</p>
                        </Banner>

                        <TextField
                            label="Multiplier (optional)"
                            type="number"
                            value={skuForm.multiplier}
                            onChange={(value) => setSkuForm({ ...skuForm, multiplier: value, fixedPrice: '' })}
                            suffix="×"
                            step="0.01"
                            placeholder="e.g., 1.60"
                        />

                        <TextField
                            label="Fixed Price (optional)"
                            type="number"
                            value={skuForm.fixedPrice}
                            onChange={(value) => setSkuForm({ ...skuForm, fixedPrice: value, multiplier: '' })}
                            prefix="$"
                            step="0.01"
                            placeholder="e.g., 29.99"
                        />

                        <TextField
                            label="Minimum Margin % (optional)"
                            type="number"
                            value={skuForm.minMargin}
                            onChange={(value) => setSkuForm({ ...skuForm, minMargin: value })}
                            suffix="%"
                            placeholder={`Default: ${currentTemplate.minimumMarginPercent}%`}
                        />

                        <TextField
                            label="Reason"
                            value={skuForm.reason}
                            onChange={(value) => setSkuForm({ ...skuForm, reason: value })}
                            placeholder="Why is this product priced differently?"
                            multiline={2}
                        />
                    </FormLayout>
                </Modal.Section>
            </Modal>

            {/* Toast */}
            {toast && (
                <Toast
                    content={toast.content}
                    error={toast.error}
                    onDismiss={() => setToast(null)}
                />
            )}
        </div>
    );
};

export default PricingRulesConfig;
